import { useEffect, useState } from "react";
import { activeSeason } from "./seasons";

function parseDay(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(y, mo - 1, d, 12, 0, 0, 0);
  return date.getFullYear() === y && date.getMonth() === mo - 1 && date.getDate() === d ? date : null;
}

// `?season=YYYY-MM-DD` pins the clock to that day (local noon) for testing.
// Honoured outside production, or when REACT_APP_SEASON_OVERRIDE === "1".
// Native builds have no URL bar: with REACT_APP_SEASON_OVERRIDE=1, the build-time
// REACT_APP_SEASON_DATE=YYYY-MM-DD fakes the day instead (device checks only —
// never ship a store build with these set).
export function seasonNow(
  search = typeof window !== "undefined" ? window.location.search : "",
  env = process.env
) {
  const flag = env.REACT_APP_SEASON_OVERRIDE === "1";
  if (env.NODE_ENV !== "production" || flag) {
    const fromQuery = parseDay(new URLSearchParams(search).get("season"));
    if (fromQuery) return fromQuery;
  }
  if (flag) {
    const fromEnv = parseDay(env.REACT_APP_SEASON_DATE);
    if (fromEnv) return fromEnv;
  }
  return new Date();
}

// A production build made with REACT_APP_SEASON_OVERRIDE=1 lets the URL or the
// build pin the calendar: fine for device checks, never for a release. Say so once,
// loudly, at startup (called from src/index.js).
let overrideWarned = false;
export function warnIfSeasonOverride(env = process.env) {
  if (overrideWarned || env.NODE_ENV !== "production" || env.REACT_APP_SEASON_OVERRIDE !== "1") return;
  overrideWarned = true;
  console.warn(
    "[Puzzly] season date override active — never ship this build (REACT_APP_SEASON_OVERRIDE=1"
      + (env.REACT_APP_SEASON_DATE ? `, REACT_APP_SEASON_DATE=${env.REACT_APP_SEASON_DATE}` : "") + ")"
  );
}

function msToNextMidnight(now) {
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 50);
  return Math.max(next.getTime() - now.getTime(), 1000);
}

export function useActiveSeason({ released } = {}) {
  const compute = () => {
    const now = seasonNow();
    return { season: activeSeason(now, released), now };
  };
  const [state, setState] = useState(compute);

  useEffect(() => {
    let timer;
    const refresh = () => {
      clearTimeout(timer);
      const next = compute();
      setState((prev) =>
        prev.season === next.season && prev.now.getTime() === next.now.getTime() ? prev : next
      );
      timer = setTimeout(refresh, msToNextMidnight(new Date()));
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    refresh();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [released]);

  return state;
}
